import { defaults } from '@/api'
import { apiFetch } from '@/api/apiFetch'
import { apiConfig } from '@/config'

/**
 * Feature flags pilotés depuis la console d'admin (TEC-1701) : la gateway évalue le ciblage (rôles,
 * utilisateurs) et ne renvoie que les clés visibles pour l'utilisateur connecté.
 */
export enum FeatureNames {
  hscode = 'hscodes',
  measureComponents = 'measure_components',
  validationRules = 'validation_rules',
  rowActions = 'row_actions',
  extractionModelToggle = 'extraction_model_toggle',
  uploadLayerSelect = 'upload_layer_select',
  manualError = 'manual_error',
  referentials = 'referentials',
  duplicateTestTag = 'duplicate_test_tag',
  hyperline = 'hyperline',
  // Web Speech API : l'audio part chez Google, réservé à l'équipe en attendant la transcription backend.
  assistantDictation = 'assistant_dictation',
  // Sans fonctionnalité derrière, pas repris en base : faux pour tous.
  advancedSummaries = 'advanced_summaries',
  createSubDeclaration = 'create_sub_declaration',
  mergeAutomations = 'merge_automations',
  // Bandeau d'annonce de la préparation intelligente, à retirer une fois l'annonce passée.
  smartPreparationBanner = 'smart_preparation_banner',
  // Préparation intelligente (modale de création, préférences, Agents IA, automatisations email,
  // indicateur), ouverte à un pilote avant sa sortie pour tous.
  smartPreparation = 'smart_preparation',
  // Product tours pilotés depuis la console (proto sur la beta de /declaration).
  productTours = 'product_tours',
  // Tarifill (TEC-1807) : classement tarifaire de la beta de /declaration (bouton, fenêtre, repères
  // de la colonne code). Sans lui, rien de Tarifill ne s'affiche ni ne se charge.
  tarifill = 'tarifill',
  // Démo de Tarifill hors local (mesures, droits et RITA fictifs, en lecture seule) tant que le
  // back ne les expose pas (TEC-1834). Pour les pilotes de dev et de staging, jamais en prod.
  tarifillDemo = 'tarifill_demo',
  // Rôles et permissions : écrans ouverts aussi selon le mode de l'organisation (`useAuthzScreens`).
  rolesPermissions = 'roles_permissions',
  // Routines d'instructions (TEC-1915) : banque Nabu, agence, types et option automatique.
  instructionRoutines = 'instruction_routines',
  // Généralisés : vrais pour tous, sans passer par la gateway.
  newSettings = 'new-settings',
  newSidebar = 'new-sidebar',
  newTrackingView = 'new-tracking-view',
  newArchivedView = 'new-archived-view',
  newEditLock = 'new-edit-lock',
  tableAutomations = 'table-automations',
  clientOrganizationUpload = 'client-organization-upload',
}

const ALWAYS_ENABLED_FEATURES = new Set<FeatureNames>([
  FeatureNames.newSettings,
  FeatureNames.newSidebar,
  FeatureNames.newTrackingView,
  FeatureNames.newArchivedView,
  FeatureNames.newEditLock,
  FeatureNames.tableAutomations,
  FeatureNames.clientOrganizationUpload,
])

/*
 * Relecture toutes les 5 minutes, et au retour sur l'onglet si les flags ont plus de 5 minutes : un changement fait
 * dans la console s'applique dans ce délai, sans rechargement. 15 s jusqu'au 04/11/2026, soit 5 800 appels par jour
 * en production pour des flags qui changent rarement.
 */
export const FEATURE_FLAGS_REFRESH_MS = 5 * 60_000

export const featureFlagsQueryKey = (userId?: string | null) => ['account-features', userId ?? null] as const

export const isFeatureEnabledIn = (
  visibleKeys: readonly string[] | undefined,
  feature: FeatureNames
): boolean => ALWAYS_ENABLED_FEATURES.has(feature) || (visibleKeys?.includes(feature) ?? false)

const authHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const base = defaults.headers ?? {}
  if (typeof base === 'object' && base !== null) {
    for (const [key, value] of Object.entries(base)) {
      if (typeof value === 'string') headers[key] = value
    }
  }
  return headers
}

/** Clés des flags visibles pour l'utilisateur connecté (GET /account/features). */
export async function fetchAccountFeatures(): Promise<string[]> {
  const response = await apiFetch(`${apiConfig().apiUrl}/account/features`, {
    method: 'GET',
    headers: authHeaders(),
    cache: 'no-store',
  })
  if (!response.ok) {
    throw new Error(`GET /account/features failed with status ${response.status}`)
  }
  const body = (await response.json()) as { data: string[] }
  return body.data
}
